import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import type { MapMode } from "@/types";

import TopBarModes from "./mode";

/**
 * Props for the TopBar component.
 */
interface TopBarProps {
    /**
     * The top bar display options.
     */
    options: { autoHideTopBar: boolean };
    /**
     * Whether the top bar is currently expanded.
     */
    visibility: boolean;
    /**
     * Callback to expand or collapse the top bar.
     */
    setVisibility: (visible: boolean) => void;
    /**
     * The current map mode, selecting which top bar variant is rendered.
     */
    mapMode: MapMode;
}

const TopBarComponent = ({
    options,
    visibility,
    setVisibility,
    mapMode,
}: TopBarProps): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = useCallback(
        (event: MouseEvent | TouchEvent) => {
            const target = event.target as HTMLElement;
            const isInsideContainer =
                containerRef.current && containerRef.current.contains(target);
            const isInsidePopover = target.closest(
                ".MuiPopover-root, .MuiMenu-root, .MuiModal-root",
            );

            if (!isInsideContainer && !isInsidePopover) {
                if (visibility && options.autoHideTopBar) {
                    setVisibility(false);
                }
            }
        },
        [visibility, options.autoHideTopBar, setVisibility],
    );

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [handleClickOutside]);

    const ModeComponent = TopBarModes[mapMode.name];

    return (
        <div ref={containerRef}>
            <ModeComponent />
        </div>
    );
};

const mapStateToProps = ({ topBar, map }) => ({
    options: topBar.options,
    visibility: topBar.visibility,
    mapMode: map.general.mode,
});

const mapDispatchToProps = {
    setVisibility: actions.topBar.setVisibility,
};

export const TopBar = connect(
    mapStateToProps,
    mapDispatchToProps,
)(TopBarComponent);
